import { FilterID, type UnreadCount } from "@daangn/idl/rocketchat/api/model";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { getUnixTime } from "date-fns";
import { createNamespace } from "../redux/createNamespace";
import { type Channel, defaultPagingKey } from "./channel.entity";
import type { ChannelAggregationData } from "./channel.entity";

const namespace = createNamespace("channel");

export function sortComparer(a: Channel, b: Channel): number {
  const aUnreadCount = typeof a.unread_count === "number" ? a.unread_count : 0;
  const bUnreadCount = typeof b.unread_count === "number" ? b.unread_count : 0;

  const aTimeSeconds = a.last_message_time;
  const bTimeSeconds = b.last_message_time;

  // last_message_time.seconds 문자열을 숫자로 변환하여 비교 (undefined/null/빈 문자열인 경우 0으로 처리)
  const aTimeNum = aTimeSeconds ? getUnixTime(new Date(aTimeSeconds)) : 0;
  const bTimeNum = bTimeSeconds ? getUnixTime(new Date(bTimeSeconds)) : 0;

  if (aUnreadCount > 0 && bUnreadCount > 0) {
    return bTimeNum - aTimeNum;
  }

  // 1. 한쪽만 unread_count > 0인 경우, unread_count > 0인 채널을 우선 정렬
  if (aUnreadCount > 0 && bUnreadCount === 0) {
    return -1;
  }

  if (bUnreadCount > 0 && aUnreadCount === 0) {
    return 1;
  }

  // 2. 그 외 모든 케이스는 last_message_time을 기준으로 최신순 정렬
  return bTimeNum - aTimeNum;
}

const channelAdapter = createEntityAdapter<Channel>({
  sortComparer, // 구현한 sortComparer 사용
});

const channelMetaInformation: {
  unreadCount: UnreadCount;
  nextPagingKey: ChannelAggregationData["nextPagingKey"];
  filterId: FilterID;
  selectedChannelId: string;
} = {
  unreadCount: {
    /** 읽지 않은 메시지 수 + 알림 수 */
    total: 0,
    /** 읽지 않은 메시지 수 */
    message: 0,
    /** 읽지 않은 알림 수 */
    notification: 0,
    /** 읽지 않은 일반 채팅 메시지 수 */
    user_message: 0,
    /**
     * 읽지 않은 비즈 채팅 메시지 수
     * Key: BizAccount ID
     * Value: BizAccount에 속한 채팅 메시지 수
     */
    biz_message: {},
  },
  nextPagingKey: defaultPagingKey,
  filterId: FilterID.FILTER_ID_UNSPECIFIED,
  selectedChannelId: "",
};
const initialState = channelAdapter.getInitialState(channelMetaInformation);

export const {
  reducer,
  actions: {
    updateChannelAggregationData,
    setSelectedChannelId,
    upsertChannel,
    updateChannel,
    upsertChannelList,
    removeChannel,
    removeChannelList,
    clearChannelList,
  },
} = createSlice({
  name: namespace,
  initialState,
  reducers: {
    updateChannelAggregationData: (
      state,
      action: PayloadAction<Partial<ChannelAggregationData>>,
    ) => {
      state.unreadCount = action.payload.unreadCount ?? state.unreadCount;
      state.nextPagingKey = action.payload.nextPagingKey ?? state.nextPagingKey;
      state.filterId = action.payload.filterId ?? state.filterId;
    },
    setSelectedChannelId: (state, action: PayloadAction<string>) => {
      state.selectedChannelId = action.payload;
    },
    upsertChannel: channelAdapter.upsertOne,
    updateChannel: channelAdapter.updateOne,
    upsertChannelList: channelAdapter.upsertMany,
    removeChannel: channelAdapter.removeOne,
    removeChannelList: channelAdapter.removeMany,
    clearChannelList: channelAdapter.removeAll,
  },
});

export const createReducerType = (typename: string) =>
  `${namespace}/${typename}`;

const selectors = channelAdapter.getSelectors();
export const channelSelectors = {
  selectIds: selectors.selectIds,
  selectById: (
    ...params: Parameters<typeof selectors.selectById>
  ): Channel | undefined => {
    return selectors.selectById(...params);
  },
  selectEntities: selectors.selectEntities,
  selectTotal: selectors.selectTotal,
  selectAll: selectors.selectAll,
};
